import {
  AfterContentInit,
  Component,
  ContentChildren,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges
} from "@angular/core";
import {TabHeadComponent} from "./tab-head/tab-head.component";
import {TabContentComponent} from "./tab-content/tab-content.component";
import {Subscription} from "rxjs";
import {OnDestroy} from "@angular/core";
import {TabSubHeadComponent} from "./tab-subhead/tab-subhead.component";
import {UserSubscription, UserSubscriptionType} from "../../../user/user-subscription";

@Component({
    selector: 'db-tabs',
    templateUrl: './tabs.component.html',
    styleUrls: ['./tabs.component.scss'],
    standalone: false
})
export class TabsComponent implements AfterContentInit, OnDestroy, OnChanges {

  @Input() activeTabId: string;
  @Input() activeSubTabId: string;
  @Input() currentSubscription?: UserSubscription;
  @Output() onActiveTabChange: EventEmitter<{oldTabId: string, tabId: string}>;
  @Output() onActiveSubTabChange: EventEmitter<{oldTabId: string, tabId: string}>;

  @ContentChildren(TabHeadComponent) tabHeads: QueryList<TabHeadComponent>;
  @ContentChildren(TabSubHeadComponent) tabSubHeads: QueryList<TabSubHeadComponent>;
  @ContentChildren(TabContentComponent) tabContents: QueryList<TabContentComponent>;

  private _subscriptions: Subscription[];
  private _tabHeadClickSubscriptions: Subscription[];
  private _tabSubHeadClickSubscriptions: Subscription[];
  public hasContent: boolean = false;

  constructor() {
    this.onActiveTabChange = new EventEmitter<{oldTabId: string, tabId: string}>();
    this.onActiveSubTabChange = new EventEmitter<{oldTabId: string, tabId: string}>();
    this._subscriptions = [];
  }

  ngAfterContentInit() {
    this.initTabHeads();
    this.initSubTabHeads();
    this.addEventHandlers();
    this.hasContent = this.tabSubHeads.length > 0;
  }

  private initTabHeads(): void {
    if (this.tabHeads && this.tabHeads.length) {
      const validActiveTabId: string = this.getValidActiveTabId(this.activeTabId, this.tabHeads.map((tabHead: TabHeadComponent) => tabHead.tabId));
      if (this.activeTabId !== validActiveTabId) {
        setTimeout((): void => {
          this.updateActiveTabId(validActiveTabId);
        });
      }
      else {
        setTimeout(() => {
          this.updateChildrenActiveTabId(validActiveTabId);
        }, 100);
      }

      this.addTabHeadClickSubscriptions();
    }
  }

  private initSubTabHeads(): void {
    if (this.tabSubHeads && this.tabSubHeads.length) {
      const validActiveTabId: string = this.getValidActiveSubTabId(this.activeSubTabId, this.tabSubHeads.map((tabSubHead: TabSubHeadComponent) => tabSubHead.subTabId));
      if (this.activeSubTabId !== validActiveTabId) {
        setTimeout((): void => {
          this.updateActiveTabId(validActiveTabId);
        });
      }
      else {
        setTimeout(() => {
          this.updateChildrenActiveTabId(validActiveTabId);
        }, 100);
      }

      this.addTabHeadClickSubscriptions();
    }
  }

  private getValidActiveTabId(activeTabId: string, tabHeadIds: string[]): string {
    return tabHeadIds.indexOf(activeTabId) !== -1 ? activeTabId : tabHeadIds[0];
  }

  private getValidActiveSubTabId(activeSubTabId: string, tabHeadIds: string[]): string {
    return tabHeadIds.indexOf(activeSubTabId) !== -1 ? activeSubTabId : tabHeadIds[0];
  }

  private addEventHandlers(): void {
    this._subscriptions.push(this.tabHeads.changes.subscribe((): void => {
      this.removeTabHeadClickSubscriptions();
      this.initTabHeads();
    }));
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['activeTabId']) {
      setTimeout( () => {
        this.updateChildrenActiveTabId(this.activeTabId);
      }, 100);    }
  }

  private updateActiveTabId(tabId: string): void {
    let _activeTabId = null;
    if (tabId !== this.activeTabId) {
      const oldTabId: string = this.activeTabId;
      const showUpgradePopUp = tabId === "upload" && this.currentSubscription && this.currentSubscription.type === UserSubscriptionType.FREE;
      if (showUpgradePopUp) {
        _activeTabId = this.activeTabId;
      }
      else {
        this.activeTabId = tabId;
      }

      this.onActiveTabChange.emit({
        oldTabId: oldTabId,
        tabId: showUpgradePopUp ? tabId : this.activeTabId
      });
    }
    setTimeout( () => {
      this.updateChildrenActiveTabId(_activeTabId || this.activeTabId);
    }, 100);
  }

  private updateActiveSubTabId(tabId: string): void {
    if (tabId !== this.activeSubTabId) {
      const oldTabId: string = this.activeSubTabId;
      this.activeSubTabId = tabId;

      this.onActiveSubTabChange.emit({
        oldTabId: oldTabId,
        tabId: this.activeSubTabId
      });
    }
    setTimeout( () => {
      this.updateChildrenActiveTabId(this.activeSubTabId);
    }, 100);
  }

  private updateChildrenActiveTabId(tabId: string): void {
    this.tabHeads && this.tabHeads.forEach((tabHead: TabHeadComponent): void => {
      if (tabHead.activeTabId !== tabId) {
        tabHead.activeTabId = tabId;
      }
    });

    this.tabSubHeads && this.tabSubHeads.forEach((tabSubHeads: TabSubHeadComponent): void => {
      if (tabSubHeads.activeSubTabId !== tabId) {
        tabSubHeads.activeSubTabId = tabId;
      }
    });

    this.tabContents && this.tabContents.forEach((tabContent: TabContentComponent): void => {
      if (tabContent.activeTabId !== tabId) {
        tabContent.activeTabId = tabId;
      }
    });
  }

  ngOnDestroy(): void {
    this.removeEventHandlers();
  }

  private removeEventHandlers(): void {
    this.removeTabHeadClickSubscriptions();

    this._subscriptions.forEach((subscription: Subscription): void => {
      subscription.unsubscribe();
    });
    this._subscriptions = null;
  }

  private removeTabHeadClickSubscriptions(): void {
    if (this._tabHeadClickSubscriptions) {
      this._tabHeadClickSubscriptions.forEach((subscription: Subscription) => {
        subscription.unsubscribe();
      });

      this._tabHeadClickSubscriptions = null;
    }

    if (this._tabSubHeadClickSubscriptions) {
      this._tabSubHeadClickSubscriptions.forEach((subscription: Subscription) => {
        subscription.unsubscribe();
      });

      this._tabSubHeadClickSubscriptions = null;
    }
  }

  private addTabHeadClickSubscriptions(): void {

    this._tabHeadClickSubscriptions = [];
    this._tabSubHeadClickSubscriptions = [];
    this.tabHeads.forEach((tabHead: TabHeadComponent): void => {
      this._tabHeadClickSubscriptions.push(tabHead.onTabHeadClick.subscribe(($event): void => {
        this.onTabHeadClick($event);
      }));
    });

    this.tabSubHeads.forEach((tabSubHead: TabSubHeadComponent): void => {
      this._tabSubHeadClickSubscriptions.push(tabSubHead.onTabSubHeadClick.subscribe(($event): void => {
        this.onTabSubHeadClick($event);
      }));
    });
  }

  public onTabHeadClick($event): void {
    this.updateActiveTabId($event.tabId);
  }

  public onTabSubHeadClick($event): void {
    this.updateActiveSubTabId($event.tabId);
  }


}
